import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { authQuery } from '@/lib/client/auth-query';
import { createSupabaseBrowserClient } from '@/lib/supabase/client';

import { QueryKeys } from '@/constants/query-keys';
import type { AppLocale } from '@/i18n/locales';

export const useLocations = () => {
  return useQuery({
    queryKey: [QueryKeys.LOCATIONS],
    staleTime: 5 * 60 * 1000,
    queryFn: authQuery(async ({ supabase }) => {
      const { data, error } = await supabase.from('locations').select('*').order('created_at');
      if (error) throw error;
      return data;
    }),
  });
};

export const useLocationsSimple = () => {
  return useQuery({
    queryKey: [QueryKeys.LOCATIONS, 'simple'],
    queryFn: async () => {
      const supabase = createSupabaseBrowserClient();
      console.log('Direct locations query...');
      const { data, error } = await supabase.from('locations').select('*');
      console.log('Direct locations result:', { data, error });
      if (error) throw error;
      return data;
    },
  });
};


export const useCurrentLocation = () => {
  const { data: locations } = useLocations();

  return useQuery({
    queryKey: [QueryKeys.CURRENT_LOCATION],
    staleTime: 5 * 60 * 1000,
    enabled: !!locations,
    queryFn: authQuery(async ({ user }) => {
      const locationId = user?.user_metadata?.location_id;
      if (!locationId || !locations) return null;
      return locations.find((l) => l.id === locationId) ?? null;
    }),
  });
};

export const useUpdateLocationLanguage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      locationId,
      language,
    }: {
      locationId?: string | null;
      language: AppLocale;
    }) => {
      const res = await fetch('/api/locale', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ locale: language }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          typeof body?.error === 'string' ? body.error : 'Failed to set locale',
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.LOCATIONS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CURRENT_LOCATION] });
    },
    onError: (error) => {
      toast.error('Failed to update language', {
        description: error.message || 'Please try again.',
      });
    },
  });
};

// API function to insert a new location
export const insertLocation = async (name: string) => {
  const supabase = createSupabaseBrowserClient();
  
  const { data, error } = await supabase
    .from('locations')
    .insert({ name })
    .select()
    .single();
    
  if (error) throw new Error(error.message);
  return data;
};

// API function to upsert a location (insert or update)
export const upsertLocation = async (locationData: {
  id?: string;
  name: string;
  photosUrl?: string[];
  logo_url?: string | null;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  trn?: string;
  salonName?: string;
  vat_option?: 'inclusive' | 'exclusive';
  /** Decimal fraction, e.g. 0.05 for 5% */
  vat_rate?: number | null;
  currency_code?: string | null;
}) => {
  const supabase = createSupabaseBrowserClient();

  const dataToUpsert = {
    ...locationData,
    photosUrl: locationData.photosUrl === undefined ? null : locationData.photosUrl,
    logo_url:
      locationData.logo_url === undefined ? undefined : locationData.logo_url,
    email: locationData.email?.trim() || null,
    phone: locationData.phone?.trim() || null,
    address: locationData.address?.trim() || null,
    city: locationData.city?.trim() || null,
    country: locationData.country?.trim() || null,
    trn: locationData.trn?.trim() || null,
    salonName: locationData.salonName?.trim() || null,
    vat_option: locationData.vat_option ?? 'inclusive',
    vat_rate:
      locationData.vat_rate === undefined ? undefined : locationData.vat_rate,
    currency_code:
      locationData.currency_code === undefined ? undefined : locationData.currency_code,
  };
  
  const { data, error } = await supabase
    .from('locations')
    .upsert(dataToUpsert, { 
      onConflict: 'id',
      ignoreDuplicates: false 
    })
    .select()
    .single();
    
  if (error) throw new Error(error.message);
  return data;
};

// Mutation hook to insert a new location
export const useInsertLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: insertLocation,
    onSuccess: (data) => {
      // Invalidate and refetch locations
      queryClient.invalidateQueries({ queryKey: [QueryKeys.LOCATIONS] });
      toast.success('Branch created successfully!', {
        description: `${data.name} has been added to your branches.`,
      });
    },
    onError: (error) => {
      toast.error('Failed to create branch', {
        description: error.message || 'Please try again.',
      });
    },
  });
};

// Mutation hook to upsert a location (insert or update)
export const useUpsertLocation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: upsertLocation,
    onSuccess: (data) => {
      // Invalidate and refetch locations
      queryClient.invalidateQueries({ queryKey: [QueryKeys.LOCATIONS] });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.CURRENT_LOCATION],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.TEAM_MEMBERS, 'by-location', data.id] });
      
      const isUpdate = !!data.id;
      toast.success(isUpdate ? 'Branch updated successfully!' : 'Branch created successfully!', {
        description: `${data.name} has been ${isUpdate ? 'updated' : 'added to your branches'}.`,
      });
    },
    onError: (error) => {
      toast.error('Failed to save branch', {
        description: error.message || 'Please try again.',
      });
    },
  });
};
